import React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/lib/api';
import { Icons } from '@/components/ui/icons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { Id } from '../../../convex/_generated/dataModel';
import { formatBerlinDateWithOptions } from '@/lib/berlin-time';

interface ProviderBankTransferInstructionsProps {
  workInvoiceId: Id<'workInvoices'>;
  amount: number;
  issuedAt: number;
}

export const ProviderBankTransferInstructions: React.FC<ProviderBankTransferInstructionsProps> = ({ workInvoiceId }) => {
  const invoiceStatus = useQuery(api.app.billing.getWorkInvoiceWithPaymentStatus, {
    workInvoiceId,
  });

  if (!invoiceStatus) return null;

  const isConfirmed = invoiceStatus.status === 'paid' && invoiceStatus.manualPaymentConfirmedAt;
  const isPending = invoiceStatus.status === 'issued';

  return (
    <div className='space-y-4'>
      {/* Payment Status */}
      {isConfirmed ? (
        <div className='p-4 rounded-md bg-status-success-light border border-status-success-border'>
          <div className='flex items-center gap-2 text-status-success'>
            <Icons.Check className='h-5 w-5' />
            <div>
              <div className='font-medium'>Zahlungseingang bestätigt</div>
              <div className='text-sm opacity-80'>
                Der Vertreter hat den Zahlungseingang am{' '}
                {invoiceStatus.manualPaymentConfirmedAt &&
                  formatBerlinDateWithOptions(invoiceStatus.manualPaymentConfirmedAt, {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}{' '}
                bestätigt
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='p-4 rounded-md bg-status-info-light border border-status-info-border'>
          <div className='flex items-start gap-2 text-status-info'>
            <Icons.Info className='h-5 w-5 mt-0.5' />
            <div className='flex-1'>
              <div className='font-medium mb-2'>Bitte überweisen Sie den Betrag per Banküberweisung</div>

              {/* Bank Details */}
              {invoiceStatus.substituteBankDetails && (
                <div className='mt-3 p-3 rounded-md bg-card border border-status-info-border'>
                  <div className='font-medium mb-2'>Bankverbindung des Vertreters:</div>
                  <div className='text-sm font-mono space-y-1.5'>
                    <div className='flex justify-between'>
                      <span className='opacity-70'>IBAN:</span>
                      <span className='font-semibold'>{invoiceStatus.substituteBankDetails.iban}</span>
                    </div>
                    {invoiceStatus.substituteBankDetails.bic && (
                      <div className='flex justify-between'>
                        <span className='opacity-70'>BIC:</span>
                        <span className='font-semibold'>{invoiceStatus.substituteBankDetails.bic}</span>
                      </div>
                    )}
                    {invoiceStatus.substituteBankDetails.accountHolder && (
                      <div className='flex justify-between'>
                        <span className='opacity-70'>Kontoinhaber:</span>
                        <span className='font-semibold'>{invoiceStatus.substituteBankDetails.accountHolder}</span>
                      </div>
                    )}
                    {invoiceStatus.substituteBankDetails.bankName && (
                      <div className='flex justify-between'>
                        <span className='opacity-70'>Bank:</span>
                        <span className='font-semibold'>{invoiceStatus.substituteBankDetails.bankName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reminder Count */}
              {invoiceStatus.paymentReminderCount && invoiceStatus.paymentReminderCount > 0 && (
                <div className='mt-3 text-sm opacity-80'>
                  <Icons.Bell className='h-3 w-3 inline mr-1' />
                  Der Vertreter hat {invoiceStatus.paymentReminderCount} Zahlungserinnerung
                  {invoiceStatus.paymentReminderCount > 1 ? 'en' : ''} gesendet
                  {invoiceStatus.paymentReminderSentAt && (
                    <span className='text-xs block mt-1'>
                      Letzte Erinnerung:{' '}
                      {formatBerlinDateWithOptions(invoiceStatus.paymentReminderSentAt, {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Information Alert */}
      {isPending && (
        <Alert>
          <Icons.AlertCircle className='h-4 w-4' />
          <AlertDescription className='text-xs'>
            <strong>Wichtig:</strong> Bitte überweisen Sie den Betrag innerhalb von 5 Werktagen. Der Vertreter wird den Zahlungseingang bestätigen, sobald die Überweisung auf seinem Konto eingegangen ist.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
